/* eslint-disable import/no-cycle */
/* Copyright (c) 2020-present CloudIO Inc */

import { useStateXValue } from '@cloudio-saas/statex';
import { getManager, PropertyValueProp } from 'componentregistry';
import { designPath } from 'designer/atoms';
import { PlatformContext } from 'platform/PlatformProvider';
import React, { memo, useContext } from 'react';
import { isEmpty } from 'utils/isEmpty';
import { getDefaultValues, getPropertyDef } from './PropertyEditorUtils';
import PropertyField from './PropertyField';
import { PropertyEditorProps } from './types';

function PropertyValueProperty({
  appUid,
  activeItemId,
  def,
  pageId,
  property,
  mgr,
  value = getDefaultValues(mgr, property),
  onChange,
  hmr,
  isViewOnlyUser = false,
}: PropertyEditorProps<PropertyValueProp, unknown>) {
  const platform = useContext(PlatformContext);
  const targetItemId = useStateXValue(
    designPath({
      appUid,
      pageId,
      itemId: activeItemId,
      key: 'props',
      subkey: def.itemIdProp,
    }),
    '',
  );
  const prop = useStateXValue(
    designPath({
      appUid,
      pageId,
      itemId: activeItemId,
      key: 'props',
      subkey: def.propertyProp,
    }),
    '',
  );

  if (isEmpty(targetItemId) || isEmpty(prop)) {
    return null;
  }
  const item = platform.getItem(appUid, pageId, targetItemId);
  if (!item?.type) {
    return null;
  }
  const tmgr = getManager(item.type);
  const tdef = getPropertyDef(tmgr, prop);

  return (
    <PropertyField
      isViewOnlyUser={isViewOnlyUser}
      appUid={appUid}
      hmr={hmr}
      // pass the actual itemId for which this prop is being rendered
      // to make the prop validations (e.g. activeTab item id validation)
      // work with respect to the targetItemId instead of the
      // current activeItemId (which is the set prop action in this case.)
      activeItemId={targetItemId}
      def={tdef}
      pageId={pageId}
      property={prop}
      mgr={tmgr}
      value={value}
      onChange={(_, val) => onChange(property, val)}
      platform={platform}
    />
  );
}

export default memo(PropertyValueProperty);
